import CallOutlinedIcon from '@mui/icons-material/CallOutlined'
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined'
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined'
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined'
import { Alert, Avatar, Button, Card, FormControl, MenuItem, Select, Skeleton } from '@mui/material'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState, type ReactNode } from 'react'
import { useNetwork } from '@/app/network/NetworkProvider'
import { PageBackHeader } from '@/components/PageBackHeader/PageBackHeader'
import { formatPhone } from '@/components/PhoneNumberField/phoneNumbers'
import { useAuth } from '@/features/auth/AuthProvider'
import { ConfirmationLabel, confirmationDetails, DeliveryLine, RemoveDialog, type Removing } from '@/features/registrations/components/MyContactsPanel/MyContactsPanel'
import { outbox, type Outbox } from '@/features/registrations/outbox'
import { registrationService, type ConfirmationStatus, type MyContact, type RegistrationService } from '@/features/registrations/registrationService'
import { smsService, SmsServiceError, type SmsDeliveryStatus, type SmsService } from '@/features/sms/smsService'
import { TicketDialog } from '@/features/tickets/components/TicketDialog/TicketDialog'
import { ticketService, TicketServiceError, type TicketService } from '@/features/tickets/ticketService'
import { saveContactToPhone } from '../../contactCard'
import { initialsFor } from '../../contactOrder'
import { useMyContact, type MyContactsSources } from '../../useMyContacts'
import styles from './ContactPage.module.scss'

const timeFormat = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
const dayFormat = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })
const eventDay = (date: string | null) => (date ? dayFormat.format(new Date(`${date}T00:00:00Z`)) : null)

/** The phone number for tel: and sms: links: digits and a leading +, nothing else. */
const dialable = (phone: string) => phone.replace(/[^\d+]/g, '')

type Props = Readonly<{
  contactId: string
  sources?: MyContactsSources
  service?: Pick<RegistrationService, 'updateConfirmationStatus' | 'remove'>
  queue?: Pick<Outbox, 'withdraw'>
  tickets?: Pick<TicketService, 'issue' | 'resolvePublic'>
  sms?: Pick<SmsService, 'readStatuses' | 'resend' | 'requestDrain'>
}>

/** One of the four quick actions: always shown, with the reason underneath when it can't be used. */
function Action({ icon, label, reason, disabled, href, onClick }: Readonly<{ icon: ReactNode; label: string; reason: string | null; disabled: boolean; href?: string | undefined; onClick?: (() => void) | undefined }>) {
  return (
    <div className={styles.action}>
      <Button className={styles.actionButton} variant="outlined" disabled={disabled} {...(href && !disabled ? { href } : {})} {...(onClick ? { onClick } : {})}>{icon}{label}</Button>
      <span className={styles.reason}>{reason ?? ''}</span>
    </div>
  )
}

/**
 * One registration as a contact card: who they are, the four quick actions
 * (Message, Call, Ticket, Resend), and its event's follow-up.
 */
export function ContactPage({ contactId, sources, service = registrationService, queue = outbox, tickets = ticketService, sms = smsService }: Props) {
  const auth = useAuth()
  const navigate = useNavigate()
  const { isOnline } = useNetwork()
  const userId = auth.session?.user.id ?? null
  const loaded = useMyContact(userId, contactId, sources)
  const [delivery, setDelivery] = useState<SmsDeliveryStatus | null>(null)
  const [busy, setBusy] = useState<'status' | 'ticket' | 'resend' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [viewingTicket, setViewingTicket] = useState(false)
  const [removing, setRemoving] = useState<Removing | null>(null)
  const [removePending, setRemovePending] = useState(false)
  const [removeError, setRemoveError] = useState<string | null>(null)

  const sent: MyContact | undefined = loaded.contact ?? undefined
  const onDevice = loaded.onDevice ?? undefined

  useEffect(() => {
    if (!sent || !isOnline) return
    let active = true
    void sms.readStatuses([sent.registrationId]).then(
      (statuses) => { if (active) setDelivery(statuses[sent.registrationId] ?? null) },
      () => undefined,
    )
    return () => { active = false }
  }, [sent, isOnline, sms])

  const back = () => void navigate({ to: '/contacts' })
  const header = <PageBackHeader backLabel="My contacts" onBack={back}>{null}</PageBackHeader>
  if (loaded.loading) return <section className={styles.page}>{header}<div role="status" aria-label="Loading contact"><Skeleton variant="circular" width={80} height={80} /><Skeleton height={48} /><Skeleton height={160} /></div></section>
  if (!sent && !onDevice) return <section className={styles.page}>{header}{loaded.error ? <Alert severity="error">{loaded.error}</Alert> : <Alert severity="info">This contact isn’t available any more. Their registration may have been removed.</Alert>}</section>

  const person = sent ?? { firstName: onDevice!.contact.firstName, lastName: onDevice!.contact.lastName ?? null, phoneNumber: onDevice!.contact.phoneNumber ?? null, emailAddress: onDevice!.contact.emailAddress ?? null, socialMedia: onDevice!.contact.socialMedia ?? null }
  const name = [person.firstName, person.lastName].filter(Boolean).join(' ')
  const phone = person.phoneNumber?.trim() || null
  const noPhone = phone ? null : 'No phone number'

  const updateStatus = async (status: ConfirmationStatus) => {
    if (!sent) return
    setBusy('status'); setError(null)
    try {
      loaded.update({ confirmationStatus: await service.updateConfirmationStatus(sent.registrationId, status) })
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The confirmation status could not be saved. Please try again.')
    } finally { setBusy(null) }
  }

  const issueTicket = async () => {
    if (!sent) return
    setBusy('ticket'); setError(null)
    try {
      const ticket = await tickets.issue(sent.registrationId)
      loaded.update({ ticketReference: ticket.reference })
      // A new ticket queues its text; send it now rather than at the next run.
      void sms.requestDrain().catch(() => undefined)
      setViewingTicket(true)
    } catch (cause) {
      setError(cause instanceof TicketServiceError ? cause.message : 'The ticket could not be issued. Please try again.')
    } finally { setBusy(null) }
  }

  const resend = async () => {
    if (!sent) return
    setBusy('resend'); setError(null)
    try {
      await sms.resend(sent.registrationId)
      const now = new Date().toISOString()
      setDelivery({ state: 'sending', reason: null, at: now, canResend: false, queuedSince: now })
      void sms.requestDrain().catch(() => undefined)
    } catch (cause) {
      setError(cause instanceof SmsServiceError ? cause.message : 'The message could not be resent. Please try again.')
    } finally { setBusy(null) }
  }

  const remove = async () => {
    if (!removing) return
    setRemovePending(true); setRemoveError(null)
    try {
      if (removing.sent) await service.remove(removing.id)
      else if (!(await queue.withdraw(removing.id))) {
        setRemoveError('They’re being sent right now. Try again in a moment, once they’ve synced.')
        return
      }
      loaded.drop()
      back()
    } catch (cause) {
      setRemoveError(cause instanceof Error ? cause.message : 'The registration could not be removed. Please try again.')
    } finally { setRemovePending(false) }
  }

  // Ticket and Resend: what they do, and why not when they can't.
  const ticketed = sent?.ticketed ?? onDevice?.ticketed ?? false
  const ticketReason = !sent ? 'Not sent yet' : !ticketed ? 'Not ticketed' : !isOnline ? 'Needs a connection' : busy === 'ticket' ? 'Issuing…' : sent.ticketReference ? null : 'Tap to issue'
  const resendReason = !sent ? 'Not sent yet'
    : !ticketed ? 'Not ticketed'
    : !sent.ticketReference ? 'No ticket yet'
    : !phone ? 'No phone number'
    : busy === 'resend' ? 'Sending…'
    : delivery?.canResend ? (isOnline ? null : 'Needs a connection')
    : delivery?.state === 'delivered' ? 'Delivered'
    : delivery?.state === 'sending' ? 'Sending'
    : delivery ? 'Can’t resend now'
    : 'Not texted yet'
  const sentAt = sent ?? (onDevice ? { capturedAt: onDevice.capturedAt, sessionName: null } : null)
  const eventLine = sent ? [eventDay(sent.eventDate), sent.sessionName].filter(Boolean).join(' · ') : 'Waiting to sync from this device'

  return (
    <section className={styles.page}>
      {header}
      <div className={styles.hero}>
        <Avatar className={styles.avatar} aria-hidden="true">{initialsFor(person)}</Avatar>
        <h1 className={styles.name}>{name}</h1>
        <span className={styles.meta}>{sent ? `Registered by you at ${sent.eventName}` : 'Registered by you · not sent yet'}</span>
      </div>
      <div className={styles.actions}>
        <Action icon={<SmsOutlinedIcon />} label="Message" reason={noPhone} disabled={!phone} href={phone ? `sms:${dialable(phone)}` : undefined} />
        <Action icon={<CallOutlinedIcon />} label="Call" reason={noPhone} disabled={!phone} href={phone ? `tel:${dialable(phone)}` : undefined} />
        <Action icon={<ConfirmationNumberOutlinedIcon />} label="Ticket" reason={ticketReason} disabled={!sent || !ticketed || !isOnline || busy !== null} onClick={() => { if (sent?.ticketReference) setViewingTicket(true); else void issueTicket() }} />
        <Action icon={<SendOutlinedIcon />} label="Resend" reason={resendReason} disabled={resendReason !== null || busy !== null} onClick={() => void resend()} />
      </div>
      {error ? <Alert severity="error">{error}</Alert> : null}
      {phone || person.emailAddress || person.socialMedia ? (
        <Card className={styles.group}>
          {phone ? <div className={styles.field}><span className={styles.fieldLabel}>mobile</span><a className={styles.fieldValue} href={`tel:${dialable(phone)}`}>{formatPhone(phone)}</a></div> : null}
          {person.emailAddress ? <div className={styles.field}><span className={styles.fieldLabel}>email</span><a className={styles.fieldValue} href={`mailto:${person.emailAddress}`}>{person.emailAddress}</a></div> : null}
          {person.socialMedia ? <div className={styles.field}><span className={styles.fieldLabel}>social</span><span className={styles.fieldValue}>{person.socialMedia}</span></div> : null}
          {/* Works offline too: the card is made on the phone from what's shown here. */}
          <button type="button" className={styles.saveContact} onClick={() => saveContactToPhone({ ...person, metAt: sent ? (sent.eventDate ? `${sent.eventName} (${eventDay(sent.eventDate)})` : sent.eventName) : null })}>
            <PersonAddAltOutlinedIcon aria-hidden="true" />Save to phone contacts
          </button>
        </Card>
      ) : null}
      <Card className={styles.group}>
        <div className={styles.eventHead}>
          <span className={styles.eventText}><span className={styles.eventName}>{sent?.eventName ?? 'Not sent yet'}</span><span className={styles.eventMeta}>{eventLine}</span></span>
          {sent ? (
            <FormControl className={styles.statusControl} size="small">
              <Select
                aria-label={`Confirmation status for ${name}`}
                disabled={!isOnline || busy !== null}
                renderValue={(value) => <ConfirmationLabel status={value} />}
                value={sent.confirmationStatus}
                onChange={(event) => void updateStatus(event.target.value)}
              >
                {(Object.keys(confirmationDetails) as ConfirmationStatus[]).map((status) => <MenuItem key={status} value={status}><ConfirmationLabel status={status} /></MenuItem>)}
              </Select>
            </FormControl>
          ) : null}
        </div>
        {sent ? <div className={styles.line}><span>Ticket</span><span className={`${styles.lineValue} ${sent.ticketReference ? '' : styles.muted}`}>{!sent.ticketed ? 'Not ticketed' : sent.ticketReference ? `#${sent.ticketReference}` : 'Not issued'}</span></div> : null}
        {sent && delivery ? <div className={styles.line}><span>Ticket text</span><span className={styles.lineValue}><DeliveryLine status={delivery} /></span></div> : null}
        {sentAt ? <div className={styles.line}><span>Registered</span><span className={`${styles.lineValue} ${styles.muted}`}>{timeFormat.format(new Date(sentAt.capturedAt))}</span></div> : null}
      </Card>
      <Button className={styles.remove} color="error" onClick={() => { setRemoveError(null); setRemoving({ id: contactId, name, sent: Boolean(sent), ticketed: Boolean(sent?.ticketReference) }) }}>Remove registration</Button>
      {removing ? <RemoveDialog removing={removing} pending={removePending} error={removeError} isOnline={isOnline} onCancel={() => setRemoving(null)} onConfirm={() => void remove()} /> : null}
      {viewingTicket && sent ? <TicketDialog registrationId={sent.registrationId} attendeeName={name} onClose={() => setViewingTicket(false)} service={tickets} /> : null}
    </section>
  )
}
